import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from '../../utils/toast';
import {
  Calendar,
  Save,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  MessageCircle,
  Heart,
  MessageSquare,
  Info,
  FileText
} from 'lucide-react';
import OffboardingStepSkeleton from './OffboardingStepSkeleton';
import HorizontalStepper from '../../components/UI/HorizontalStepper';
import { StatusBadge } from '../../components/UI/StatusBadge';
import WithdrawalModal from '../../components/WithdrawalModal';
import useWithdrawal from '../../hooks/useWithdrawal';
import useOffboardingWorkflow from '../../hooks/useOffboardingWorkflow';
import FormSelect from '../../components/UI/FormSelect';
import { getOffboardingSteps, calculateProgressPercentage } from '../../config/offboardingSteps';
import { initializeStepData } from '../../utils/workflowUtils';
import { getTodayInSingaporeTimezone } from '../../utils/dateUtils';


const OffboardingStep3 = () => {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const { handleWithdrawal } = useWithdrawal();
  const { workflow, loading, saving: hookSaving, saveStep, reloadWorkflow } = useOffboardingWorkflow(workflowId);
  const [saving, setSaving] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [stepData, setStepData] = useState({
    // Administrative fields
    employment_end_date: '',
    final_salary_payment_date: '',

    // Exit Interview Preparation
    exit_interview_scheduled: null,
    interview_format: 'in_person',
    questionnaire_sent: '',
    questionnaire_sent_date: '',

    // During Interview
    interview_welcome_completed: false,
    interview_welcome_notes: '',
    interview_confidentiality_explained: false,
    interview_confidentiality_notes: '',
    interview_purpose_explained: false,
    interview_purpose_notes: '',
    feedback_career_guidance: false,
    feedback_career_guidance_notes: '',
    feedback_career_pathways: false,
    feedback_career_pathways_notes: '',
    feedback_workplace_flexibility: false,
    feedback_workplace_flexibility_notes: '',
    feedback_workplace_culture: false,
    feedback_workplace_culture_notes: '',
    feedback_recognition: false,
    feedback_recognition_notes: '',
    feedback_image_reputation: false,
    feedback_image_reputation_notes: '',
    feedback_others: false,
    feedback_others_notes: '',
    interview_notes: '',

    // Employee Support
    reference_letter_assistance: false,
  });

  // Define the 7 steps for offboarding overview
  // Handle step click navigation
  const handleStepClick = (step) => {
    navigate(step.route);
  };

  // Initialize step data when workflow loads
  useEffect(() => {
    if (workflow) {
      const initialData = initializeStepData(workflow, 3);
      setStepData(initialData);
    }
  }, [workflow]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveStep(3, stepData);
    } catch (error) {
      console.error('Error saving step:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    navigate(`/admin/employee-lifecycle/offboarding/${workflowId}/step-4`);
  };

  const handlePrev = () => {
    navigate(`/admin/employee-lifecycle/offboarding/${workflowId}/step-2`);
  };

  if (loading) {
    return <OffboardingStepSkeleton />;
  }

  if (!workflow) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Workflow Not Found</h2>
          <p className="text-gray-600">The requested offboarding workflow could not be found.</p>
        </div>
      </div>
    );
  }

  const steps = getOffboardingSteps(workflow, workflowId);
  const progressPercentage = calculateProgressPercentage(workflow);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white">
        <div className="px-6 py-8 space-y-2">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                Step 3: HR Exit Interview
                </h1>
              <p className="text-xs sm:text-sm text-gray-500 truncate">
                  {workflow.employee.first_name} {workflow.employee.last_name} • {workflow.employee.staff_id}
                </p>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-500">Step 3 of {workflow?.requires_ir21 ? 7 : 6}</span>
              {workflow.step_3_completed && (
                <StatusBadge status="completed" showIcon />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Horizontal Stepper */}
      <HorizontalStepper
        steps={steps}
        currentStep={3}
        onStepClick={handleStepClick}
        progressPercentage={progressPercentage}
      />

      {/* Main Content */}
      <div className="px-6 py-8 space-y-8">
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="flex items-center mb-4 sm:mb-6 min-w-0">
            <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mr-2 sm:mr-3 flex-shrink-0" />
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">HR Exit Interview</h2>
          </div>

          <div className="space-y-8">

            {/* Exit Interview Preparation */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <FileText className="w-5 h-5 text-red-600 mr-2" />
                Exit Interview Preparation
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Exit Interview Scheduled *
                  </label>
                  <input
                    type="date"
                    value={stepData.exit_interview_scheduled || getTodayInSingaporeTimezone()}
                    onChange={(e) => setStepData({...stepData, exit_interview_scheduled: e.target.value})}
                    className="input-admin w-full"
                    disabled={workflow.step_3_completed}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Interview Format
                  </label>
                  <FormSelect
                    options={[
                      { value: 'in_person', label: 'In-Person' },
                      { value: 'virtual', label: 'Virtual (Zoom/Teams)' },
                      { value: 'phone', label: 'Phone Call' },
                    ]}
                    value={stepData.interview_format || 'in_person'}
                    onChange={(val) => setStepData({...stepData, interview_format: val})}
                    theme="admin"
                    disabled={workflow.step_3_completed}
                  />
                </div>
              </div>

          </div>

            {/* During Interview - Feedback */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <MessageSquare className="w-5 h-5 text-red-600 mr-2" />
                During Interview - Feedback
              </h3>

              <div className="space-y-4">
                <h4 className="text-base font-semibold text-gray-900 mb-4">💭 Experience & Feedback</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="space-y-3">
                      <div>
                        <div className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            checked={stepData.feedback_career_guidance || false}
                            onChange={(e) => setStepData({...stepData, feedback_career_guidance: e.target.checked})}
                            className="mr-3"
                            disabled={workflow.step_3_completed}
                          />
                          <span className="text-sm text-gray-700 mr-2">Career Guidance and Mentorship</span>
                          <div className="relative group">
                            <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                            <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 shadow-lg">
                              Discuss experiences with career development programs, mentoring relationships, and guidance received for professional growth.
                            </div>
                          </div>
                        </div>
                        {stepData.feedback_career_guidance && (
                          <div className="ml-6 mb-3">
                            <input
                              type="text"
                              value={stepData.feedback_career_guidance_notes || ''}
                              onChange={(e) => setStepData({...stepData, feedback_career_guidance_notes: e.target.value})}
                              className="input-admin w-full"
                              placeholder="Feedback about career guidance..."
                              disabled={workflow.step_3_completed}
                            />
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            checked={stepData.feedback_career_pathways || false}
                            onChange={(e) => setStepData({...stepData, feedback_career_pathways: e.target.checked})}
                            className="mr-3"
                            disabled={workflow.step_3_completed}
                          />
                          <span className="text-sm text-gray-700 mr-2">Career Pathways and Progression</span>
                          <div className="relative group">
                            <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                            <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 shadow-lg">
                              Explore experiences with internal career advancement opportunities, promotion processes, and clarity of career progression paths.
                            </div>
                          </div>
                        </div>
                        {stepData.feedback_career_pathways && (
                          <div className="ml-6 mb-3">
                            <input
                              type="text"
                              value={stepData.feedback_career_pathways_notes || ''}
                              onChange={(e) => setStepData({...stepData, feedback_career_pathways_notes: e.target.value})}
                              className="input-admin w-full"
                              placeholder="Feedback about career pathways..."
                              disabled={workflow.step_3_completed}
                            />
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            checked={stepData.feedback_workplace_flexibility || false}
                            onChange={(e) => setStepData({...stepData, feedback_workplace_flexibility: e.target.checked})}
                            className="mr-3"
                            disabled={workflow.step_3_completed}
                          />
                          <span className="text-sm text-gray-700 mr-2">Workplace Flexibility and Work Arrangements</span>
                          <div className="relative group">
                            <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                            <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 shadow-lg">
                              Discuss remote work policies, flexible hours, work-life balance initiatives, and overall work arrangement satisfaction.
                            </div>
                          </div>
                        </div>
                        {stepData.feedback_workplace_flexibility && (
                          <div className="ml-6 mb-3">
                            <input
                              type="text"
                              value={stepData.feedback_workplace_flexibility_notes || ''}
                              onChange={(e) => setStepData({...stepData, feedback_workplace_flexibility_notes: e.target.value})}
                              className="input-admin w-full"
                              placeholder="Feedback about workplace flexibility..."
                              disabled={workflow.step_3_completed}
                            />
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            checked={stepData.feedback_workplace_culture || false}
                            onChange={(e) => setStepData({...stepData, feedback_workplace_culture: e.target.checked})}
                            className="mr-3"
                            disabled={workflow.step_3_completed}
                          />
                          <span className="text-sm text-gray-700 mr-2">Workplace Culture and Environment</span>
                          <div className="relative group">
                            <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                            <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 shadow-lg">
                              Explore experiences with company values, team dynamics, inclusivity, communication, and overall workplace atmosphere.
                            </div>
                          </div>
                        </div>
                        {stepData.feedback_workplace_culture && (
                          <div className="ml-6 mb-3">
                            <input
                              type="text"
                              value={stepData.feedback_workplace_culture_notes || ''}
                              onChange={(e) => setStepData({...stepData, feedback_workplace_culture_notes: e.target.value})}
                              className="input-admin w-full"
                              placeholder="Feedback about workplace culture..."
                              disabled={workflow.step_3_completed}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="space-y-3">
                      <div>
                        <div className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            checked={stepData.feedback_recognition || false}
                            onChange={(e) => setStepData({...stepData, feedback_recognition: e.target.checked})}
                            className="mr-3"
                            disabled={workflow.step_3_completed}
                          />
                          <span className="text-sm text-gray-700 mr-2">Recognition</span>
                          <div className="relative group">
                            <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                            <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 shadow-lg">
                              Discuss experiences with performance recognition, appreciation programs, awards, and feeling valued for contributions.
                            </div>
                          </div>
                        </div>
                        {stepData.feedback_recognition && (
                          <div className="ml-6 mb-3">
                            <input
                              type="text"
                              value={stepData.feedback_recognition_notes || ''}
                              onChange={(e) => setStepData({...stepData, feedback_recognition_notes: e.target.value})}
                              className="input-admin w-full"
                              placeholder="Feedback about recognition..."
                              disabled={workflow.step_3_completed}
                            />
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            checked={stepData.feedback_image_reputation || false}
                            onChange={(e) => setStepData({...stepData, feedback_image_reputation: e.target.checked})}
                            className="mr-3"
                            disabled={workflow.step_3_completed}
                          />
                          <span className="text-sm text-gray-700 mr-2">Image and Reputation</span>
                          <div className="relative group">
                            <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                            <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 shadow-lg">
                              Explore perceptions of company brand, industry reputation, stakeholder relationships, and overall organizational image.
                            </div>
                          </div>
                        </div>
                        {stepData.feedback_image_reputation && (
                          <div className="ml-6 mb-3">
                            <input
                              type="text"
                              value={stepData.feedback_image_reputation_notes || ''}
                              onChange={(e) => setStepData({...stepData, feedback_image_reputation_notes: e.target.value})}
                              className="input-admin w-full"
                              placeholder="Feedback about image and reputation..."
                              disabled={workflow.step_3_completed}
                            />
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            checked={stepData.feedback_others || false}
                            onChange={(e) => setStepData({...stepData, feedback_others: e.target.checked})}
                            className="mr-3"
                            disabled={workflow.step_3_completed}
                          />
                          <span className="text-sm text-gray-700 mr-2">Others</span>
                          <div className="relative group">
                            <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                            <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 shadow-lg">
                              Any additional feedback or topics not covered in the categories above. Use this for unique experiences or concerns.
                            </div>
                          </div>
                        </div>
                        {stepData.feedback_others && (
                          <div className="ml-6 mb-3">
                            <input
                              type="text"
                              value={stepData.feedback_others_notes || ''}
                              onChange={(e) => setStepData({...stepData, feedback_others_notes: e.target.value})}
                              className="input-admin w-full"
                              placeholder="Other feedback..."
                              disabled={workflow.step_3_completed}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Interview Notes */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Key Interview Notes
              </label>
              <input
                type="text"
                value={stepData.interview_notes || ''}
                onChange={(e) => setStepData({...stepData, interview_notes: e.target.value})}
                className="input-admin w-full"
                placeholder="Record key insights, feedback, and important points discussed during the exit interview..."
                disabled={workflow.step_3_completed}
              />
            </div>

            {/* Employee Support & Resources */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <Heart className="w-5 h-5 text-red-600 mr-2" />
                Employee Support & Resources
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-base font-semibold text-gray-900 mb-3">Career Transition Support</h4>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={stepData.reference_letter_assistance || false}
                      onChange={(e) => setStepData({...stepData, reference_letter_assistance: e.target.checked})}
                      className="mr-2"
                      disabled={workflow.step_3_completed}
                    />
                    <span className="text-sm text-gray-700">Reference letter assistance</span>
                  </div>
                </div>
              </div>

            </div>
          </div>

            {/* Final Arrangements & Administrative */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <Calendar className="w-5 h-5 text-red-600 mr-2" />
                Final Arrangements & Administrative
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 space-y-6 md:space-y-0">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Employment End Date *
                  </label>
                  <input
                    type="date"
                    value={stepData.employment_end_date}
                    onChange={(e) => setStepData({...stepData, employment_end_date: e.target.value, final_salary_payment_date: e.target.value})}
                    className="input-admin w-full"
                    disabled={workflow.step_3_completed}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Final Salary Payment Date *
                  </label>
                  <input
                    type="date"
                    value={stepData.final_salary_payment_date}
                    readOnly
                    className="input-admin w-full bg-gray-50"
                    disabled={workflow.step_3_completed}
                  />
                </div>

              </div>
            </div>
          </div>

          {!workflow.step_3_completed && workflow.workflow_status !== 'withdrawn' && (
            <div className="mt-8 flex justify-between">
              <button
                onClick={handlePrev}
                className="btn btn-admin-outline"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous Step
              </button>

              <div className="flex space-x-3">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn btn-admin-secondary"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Progress'}
                </button>

                {!workflow.step_3_completed && workflow.workflow_status !== 'withdrawn' && (
                  <button
                    onClick={() => setShowWithdrawalModal(true)}
                    className="btn btn-danger"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Withdraw
                  </button>
                )}

                <button
                  onClick={handleNext}
                  className="btn btn-admin-primary"
                >
                  Next Step
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
          )}

          {workflow.step_3_completed && (
            <div className="mt-8 flex justify-between">
              <button
                onClick={handlePrev}
                className="btn btn-admin-outline"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous Step
              </button>

              <button
                onClick={handleNext}
                className="btn btn-admin-primary"
              >
                Next Step
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          )}
        </div>

        {/* Withdrawal Modal */}
        <WithdrawalModal
          isOpen={showWithdrawalModal}
          onClose={() => setShowWithdrawalModal(false)}
          currentStep={3}
          onConfirmWithdraw={handleWithdrawal}
          workflow={workflow}
          onSuccess={() => reloadWorkflow()}
        />
        </div>
      </div>
  );
};

export default OffboardingStep3;
